import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Popover,
  TextField,
  List,
  ListItemButton,
  Checkbox,
  ListItemText,
  CircularProgress,
  Typography,
} from "@mui/material";
// Iconsax ArrowDown2 (2026-09-04, matching the AI Agents pass) -- MUI's
// ArrowDropDownIcon is a heavier filled triangle, a different shape than the
// chevron used everywhere else in Reports' own filter bar.
import { ArrowDown2 } from "iconsax-react";
import { fetchBroadcastFilterOptions } from "../../redux/Slice/Reports/BroadcastsReportSlice";

// One filter chip -> popover with a search bar + multi-select list, backed by
// reportController.js's getBroadcastFilterOptions. Used for all 4 of the Broadcasts
// report's shared filters (broadcast, sent by, dataset, label) — see BroadcastsReport.jsx.
const ReportFilterDropdown = ({ field, label, selected, onChange }) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const debounceRef = useRef(null);

  const { items, loading } = useSelector((state) => state.broadcastsReport.filterOptions[field]);

  const open = Boolean(anchorEl);

  // Debounced type-ahead search — only queries while the popover is open.
  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(fetchBroadcastFilterOptions({ field, search }));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [dispatch, field, search, open]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    dispatch(fetchBroadcastFilterOptions({ field, search: "" }));
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const isSelected = (value) => selected.some((option) => String(option.value) === String(value));

  const toggleOption = (option) => {
    if (isSelected(option.value)) {
      onChange(selected.filter((o) => String(o.value) !== String(option.value)));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <Box>
      <Button
        variant={selected.length ? "contained" : "outlined"}
        size="small"
        onClick={handleOpen}
        endIcon={<Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>}
        sx={{
          textTransform: "none",
          ...(selected.length ? {} : { color: "text.primary", borderColor: "divider" }),
        }}
      >
        {label}
        {selected.length ? ` (${selected.length})` : ""}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ width: 260, p: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <List dense sx={{ maxHeight: 260, overflowY: "auto", mt: 1 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : items.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: "block", py: 1 }}>
                No matches
              </Typography>
            ) : (
              items.map((option) => (
                <ListItemButton key={option.value} dense onClick={() => toggleOption(option)}>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={isSelected(option.value)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText primary={option.label} />
                </ListItemButton>
              ))
            )}
          </List>

          {selected.length > 0 && (
            <Button size="small" onClick={() => onChange([])} sx={{ textTransform: "none", mt: 0.5 }}>
              Clear {label.toLowerCase()}
            </Button>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default ReportFilterDropdown;
